import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { parseLineItemPrefs } from '../lib/line-item-prefs';
import type { LineItemType } from '../constants/line-item-types';

/** React Query key for the company's line item preferences. */
export const LINE_ITEM_PREFS_QUERY_KEY = ['line-item-prefs'] as const;

/**
 * The set of line item types this company hides.
 *
 * Read-only and advisory: a picker or grid that cannot reach the endpoint
 * offers every type, which is the pre-feature behaviour and loses nothing.
 * That is also what the field editor gets in a dead zone — React Query's
 * cache is in-memory, so the first open of the app offline shows all 22.
 * A read degrading to the full list is acceptable; a write is never gated
 * on this.
 */
export function useHiddenLineItemTypes(): {
  hidden: ReadonlySet<LineItemType>;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: LINE_ITEM_PREFS_QUERY_KEY,
    queryFn: () => api.lineItemPrefs.get().then(r => parseLineItemPrefs(unwrap(r.data))),
    retry: false,
    staleTime: 60_000,
  });
  const hidden = useMemo(() => new Set<LineItemType>(data?.hidden ?? []), [data]);
  return { hidden, isLoading };
}
